/**
 * JourneyDayPage - Single Day Experience in the Reclaim Journey System
 * 
 * Implements the full 10-step nervous-system-first daily structure:
 * 1. Reset (mandatory entry gate)
 * 2. Enter Day button
 * 3. Video
 * 4. Embodiment Exercise (interactive)
 * 5. Regulation Practice (guided)
 * 6. Audio Experience (multi-layer)
 * 7. Reflection (journal input)
 * 8. Intention Lock-In
 * 9. Night Ritual link
 * 10. Morning Prompt (shown at start if returning)
 * 
 * Design Principles:
 * - Fluid and uninterrupted
 * - Nervous-system-first
 * - Minimal cognitive load
 * - State-first experience
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft, Play, Pause, Volume2, VolumeX, Moon,
  Wind, Heart, Eye, Check, ChevronRight, Sparkles,
  BookOpen, Target, MessageSquare, Send, Mic, X
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Step definitions - Updated flow order per Michael's spec
const STEPS = {
  MORNING: "morning",
  RESET: "reset",
  ENTER: "enter",
  VIDEO: "video",
  REGULATION: "regulation",           // Calm nervous system
  INTEGRATION_AUDIO: "integration_audio", // Lesson Integration Audio (Michael's deeper teaching)
  EMBODIMENT: "embodiment",            // Physical integration after audio
  REFLECTION: "reflection",
  INTENTION: "intention",
  COMPLETE: "complete"
};

// Updated step order: Video → Regulation → Integration Audio → Embodiment
const STEP_ORDER = [
  STEPS.MORNING,
  STEPS.RESET,
  STEPS.ENTER,
  STEPS.VIDEO,
  STEPS.REGULATION,
  STEPS.INTEGRATION_AUDIO,
  STEPS.EMBODIMENT,
  STEPS.REFLECTION,
  STEPS.INTENTION,
  STEPS.COMPLETE
];

// Reset configurations matching NetworkerResetsPage
const RESET_CONFIG = {
  breathe: { 
    icon: Wind, 
    label: "Breathe", 
    color: "sky",
    voiceAudio: "/api/voice-audio/resets/breathe.m4a",
    duration: 94
  },
  let_go: { 
    icon: Wind, 
    label: "Let Go", 
    color: "purple",
    voiceAudio: "/api/voice-audio/resets/let_go.m4a",
    duration: 75
  },
  heart: { 
    icon: Heart, 
    label: "Heart", 
    color: "rose",
    voiceAudio: "/api/voice-audio/resets/heart.m4a",
    duration: 90
  },
  still: { 
    icon: Eye, 
    label: "Stillness", 
    color: "slate",
    voiceAudio: "/api/voice-audio/resets/still.m4a",
    duration: 72
  },
};

// Reset type mapping from backend to NetworkerResetsPage IDs
const RESET_TYPE_MAP = {
  breathe: "breathe",
  let_go: "letgo",
  heart: "heart",
  still: "still",
};

export const JourneyDayPage = () => {
  const navigate = useNavigate();
  const { dayNumber } = useParams();
  const [searchParams] = useSearchParams();
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Current step tracking
  const [currentStep, setCurrentStep] = useState(STEPS.RESET);
  const [stepProgress, setStepProgress] = useState({});
  
  // UI State
  const [showMorningPrompt, setShowMorningPrompt] = useState(false);
  
  // Check if returning from reset
  const resetComplete = searchParams.get('resetComplete') === 'true';

  useEffect(() => {
    fetchDayData();
  }, [dayNumber]);
  
  // Handle return from reset page
  useEffect(() => {
    if (resetComplete && dayData) {
      // Mark reset as complete and move to video
      completeStep("reset").then(() => {
        setCurrentStep(STEPS.VIDEO);
        // Clean up URL
        navigate(`/journey/abundance/day/${dayNumber}`, { replace: true });
      });
    }
  }, [resetComplete, dayData, dayNumber, navigate]);

  const fetchDayData = async () => {
    const token = localStorage.getItem("reclaim_token");
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/journey/abundance/day/${dayNumber}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Failed to load day");
      }

      const data = await response.json();
      setDayData(data);
      
      // Initialize step based on progress
      initializeStep(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const initializeStep = (data) => {
    // Check if there's a morning prompt from previous day
    if (data.morning_prompt && data.day_number > 1) {
      setShowMorningPrompt(true);
    }
    
    // Determine starting step based on progress (updated order)
    const progress = data.progress;
    if (progress.is_complete) {
      setCurrentStep(STEPS.COMPLETE);
    } else if (!progress.reset_completed) {
      setCurrentStep(STEPS.RESET);
    } else if (!progress.video_watched) {
      setCurrentStep(STEPS.VIDEO);
    } else if (!progress.regulation_completed) {
      setCurrentStep(STEPS.REGULATION);
    } else if (!progress.integration_audio_completed) {
      setCurrentStep(STEPS.INTEGRATION_AUDIO);
    } else if (!progress.embodiment_completed) {
      setCurrentStep(STEPS.EMBODIMENT);
    } else if (!progress.reflection_submitted) {
      setCurrentStep(STEPS.REFLECTION);
    } else if (!progress.intention_set) {
      setCurrentStep(STEPS.INTENTION);
    }
  };

  const completeStep = async (step, additionalData = {}) => {
    const token = localStorage.getItem("reclaim_token");
    
    try {
      const response = await fetch(`${API_URL}/api/journey/abundance/day/${dayNumber}/complete-step`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ step, data: additionalData })
      });
      
      const result = await response.json();
      
      if (result.is_day_complete) {
        setCurrentStep(STEPS.COMPLETE);
      }
      
      return result;
    } catch (err) {
      console.error("Failed to complete step:", err);
    }
  };

  const handleStepComplete = async (step) => {
    await completeStep(step);
    
    // Move to next step
    const currentIndex = STEP_ORDER.indexOf(step);
    if (currentIndex < STEP_ORDER.length - 1) {
      // Skip ENTER step in progression since it's automatic after reset
      let nextStep = STEP_ORDER[currentIndex + 1];
      if (nextStep === STEPS.ENTER) {
        nextStep = STEP_ORDER[currentIndex + 2];
      }
      setCurrentStep(nextStep);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f] flex items-center justify-center">
        <motion.div
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="text-emerald-400/60"
        >
          Preparing your experience...
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f] p-6">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-white/60 mb-6">
          <ChevronLeft size={20} /> Back
        </button>
        <div className="text-center py-12">
          <p className="text-white/60">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0a0a12] to-[#12121f]">
      {/* Morning Prompt Overlay */}
      <AnimatePresence>
        {showMorningPrompt && dayData?.morning_prompt && (
          <MorningPromptOverlay 
            prompt={dayData.morning_prompt}
            previousIntention={dayData.previous_intention}
            onContinue={() => setShowMorningPrompt(false)}
          />
        )}
      </AnimatePresence>

      {/* Day Header */}
      <DayHeader 
        dayData={dayData} 
        currentStep={currentStep}
        onBack={() => navigate("/journey/abundance")}
      />

      {/* Step Content */}
      <AnimatePresence mode="wait">
        {currentStep === STEPS.RESET && (
          <ResetStep 
            key="reset"
            resetType={dayData.reset_type}
            dayNumber={dayNumber}
            onComplete={() => handleStepComplete("reset")}
          />
        )}

        {currentStep === STEPS.VIDEO && (
          <VideoStep 
            key="video"
            video={dayData.video}
            onComplete={() => handleStepComplete("video")}
          />
        )}

        {currentStep === STEPS.REGULATION && (
          <RegulationStep 
            key="regulation"
            regulation={dayData.regulation}
            onComplete={() => handleStepComplete("regulation")}
          />
        )}

        {currentStep === STEPS.INTEGRATION_AUDIO && (
          <IntegrationAudioStep 
            key="integration_audio"
            integrationAudio={dayData.integration_audio}
            onComplete={() => handleStepComplete("integration_audio")}
          />
        )}

        {currentStep === STEPS.EMBODIMENT && (
          <EmbodimentStep 
            key="embodiment"
            embodiment={dayData.embodiment}
            onComplete={() => handleStepComplete("embodiment")}
          />
        )}

        {currentStep === STEPS.REFLECTION && (
          <ReflectionStep 
            key="reflection"
            prompt={dayData.reflection_prompt}
            dayNumber={dayNumber}
            onComplete={(text) => {
              completeStep("reflection", { text });
              setCurrentStep(STEPS.INTENTION);
            }}
          />
        )}

        {currentStep === STEPS.INTENTION && (
          <IntentionStep 
            key="intention"
            prompt={dayData.intention_prompt}
            dayNumber={dayNumber}
            onComplete={(text) => {
              completeStep("intention", { text });
              setCurrentStep(STEPS.COMPLETE);
            }}
          />
        )}

        {currentStep === STEPS.COMPLETE && (
          <CompletionStep 
            key="complete"
            dayNumber={dayNumber}
            totalDays={dayData.total_days}
            nightRitualLink={dayData.night_ritual_link}
            onContinue={() => navigate("/journey/abundance")}
          />
        )}
      </AnimatePresence>
    </div>
  );
};


/**
 * Day Header - Shows phase, day number, and progress
 */
const DayHeader = ({ dayData, currentStep, onBack }) => {
  if (!dayData) return null;
  
  return (
    <div className="sticky top-0 z-10 bg-gradient-to-b from-[#0a0a12] to-transparent">
      <div className="p-4 flex items-center justify-between">
        <button onClick={onBack} className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors">
          <ChevronLeft size={20} className="text-white" />
        </button>
        
        {/* Phase & Day Indicator */}
        <div className="text-center">
          <p className="text-emerald-400/60 text-xs uppercase tracking-[0.15em]">
            {dayData.phase_label}
          </p>
          <p className="text-white font-heading">Day {dayData.day_number}</p>
        </div>
        
        <div className="w-10" /> {/* Spacer */}
      </div>
    </div>
  );
};


/**
 * Morning Prompt Overlay - Shows previous day's intention
 */
const MorningPromptOverlay = ({ prompt, previousIntention, onContinue }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-50 bg-gradient-to-b from-[#0a0a12] to-[#12121f] flex items-center justify-center p-6"
  >
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="max-w-md text-center space-y-8"
    >
      <Sparkles className="w-12 h-12 text-emerald-400 mx-auto" />
      
      <div className="space-y-4">
        <p className="text-white/60 text-sm">{prompt}</p>
        
        {previousIntention && (
          <div className="bg-white/5 rounded-xl p-4 border border-emerald-500/20">
            <p className="text-xs text-emerald-400/60 uppercase tracking-wider mb-2">Yesterday's Intention</p>
            <p className="text-white italic">"{previousIntention}"</p>
          </div>
        )}
      </div>
      
      <button
        onClick={onContinue}
        className="px-8 py-3 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-400 transition-colors"
      >
        Begin Today
      </button>
    </motion.div>
  </motion.div>
);


/**
 * Reset Step - Redirects to the full Reset experience
 * Uses the existing Quick Resets which are already beautifully crafted
 */
const ResetStep = ({ resetType, dayNumber, onComplete }) => {
  const navigate = useNavigate();
  const config = RESET_CONFIG[resetType] || RESET_CONFIG.breathe;
  const ResetIcon = config.icon;
  
  // Map backend reset type to NetworkerResetsPage ID
  const resetId = RESET_TYPE_MAP[resetType] || "breathe";
  
  const handleBeginReset = () => {
    // Store the return context in sessionStorage instead of URL to avoid encoding issues
    sessionStorage.setItem('journeyResetReturn', JSON.stringify({
      dayNumber: dayNumber,
      returnPath: `/journey/abundance/day/${dayNumber}`
    }));
    // Navigate to the reset page
    navigate(`/networker/resets/${resetId}?fromJourney=true`);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-8"
      >
        {/* Glowing icon */}
        <motion.div
          animate={{ 
            boxShadow: ["0 0 40px rgba(56, 189, 248, 0.2)", "0 0 60px rgba(56, 189, 248, 0.4)", "0 0 40px rgba(56, 189, 248, 0.2)"]
          }}
          transition={{ duration: 3, repeat: Infinity }}
          className={`w-28 h-28 rounded-full bg-gradient-to-br from-${config.color}-500/30 to-${config.color}-600/20 
                    flex items-center justify-center mx-auto border border-${config.color}-400/30`}
        >
          <ResetIcon className={`w-14 h-14 text-${config.color}-400`} />
        </motion.div>
        
        <div className="space-y-3">
          <p className="text-white/40 text-xs uppercase tracking-[0.2em]">Entry Reset</p>
          <h2 className="text-white text-3xl font-heading">{config.label}</h2>
          <p className="text-white/50 text-sm max-w-xs mx-auto">
            Before we begin, let's arrive fully present. This reset will prepare your nervous system.
          </p>
        </div>
        
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={handleBeginReset}
          className={`px-10 py-4 bg-gradient-to-r from-${config.color}-500 to-${config.color}-600 
                    text-white font-semibold rounded-full shadow-lg shadow-${config.color}-500/30
                    hover:shadow-${config.color}-500/50 transition-all`}
          data-testid="start-reset-btn"
        >
          Begin Reset
        </motion.button>
        
        <p className="text-white/30 text-xs">~{Math.round(config.duration / 60)} minute experience</p>
      </motion.div>
    </motion.div>
  );
};


/**
 * Video Step - Core teaching
 */
const VideoStep = ({ video, onComplete }) => {
  const [watching, setWatching] = useState(false);

  // For now, show placeholder since no video URL
  if (!video?.url) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center min-h-[80vh] px-6"
      >
        <div className="text-center space-y-6 max-w-md">
          <div className="w-full aspect-video bg-white/5 rounded-2xl flex items-center justify-center border border-white/10">
            <div className="text-center space-y-3">
              <Play className="w-12 h-12 text-white/30 mx-auto" />
              <p className="text-white/40 text-sm">Video coming soon</p>
              <p className="text-white/60 font-heading">{video?.title}</p>
            </div>
          </div>
          
          <button
            onClick={onComplete}
            className="px-8 py-3 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-400 transition-colors"
            data-testid="video-continue-btn"
          >
            Continue
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <div className="w-full max-w-lg space-y-6">
        <div className="w-full aspect-video bg-black rounded-2xl overflow-hidden">
          <video 
            src={video.url}
            controls
            onEnded={onComplete}
            className="w-full h-full"
          />
        </div>
        
        <button
          onClick={onComplete}
          className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-400 transition-colors"
        >
          Continue
        </button>
      </div>
    </motion.div>
  );
};


/**
 * Embodiment Step - Interactive tap-through exercise
 */
const EmbodimentStep = ({ embodiment, onComplete }) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const steps = embodiment?.steps || [];
  const currentExerciseStep = steps[currentStepIndex];

  const handleNext = () => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      onComplete();
    }
  };

  if (!isActive) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center min-h-[80vh] px-6"
      >
        <div className="text-center space-y-8 max-w-md">
          <div className="w-20 h-20 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto">
            <BookOpen className="w-10 h-10 text-amber-400" />
          </div>
          
          <div className="space-y-2">
            <p className="text-white/40 text-xs uppercase tracking-[0.2em]">Embodiment</p>
            <h2 className="text-white text-2xl font-heading">{embodiment?.title}</h2>
            <p className="text-white/50 text-sm">{embodiment?.description}</p>
          </div>
          
          <button
            onClick={() => setIsActive(true)}
            className="px-8 py-4 bg-amber-500 text-white font-semibold rounded-full hover:bg-amber-400 transition-all"
            data-testid="start-embodiment-btn"
          >
            Begin Exercise
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <div className="text-center space-y-8 max-w-md">
        {/* Progress dots */}
        <div className="flex justify-center gap-2">
          {steps.map((_, idx) => (
            <div
              key={idx}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentStepIndex ? "bg-amber-400 w-6" : 
                idx < currentStepIndex ? "bg-amber-500" : "bg-white/20"
              }`}
            />
          ))}
        </div>

        {/* Step content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStepIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6"
          >
            <p className="text-white text-xl leading-relaxed">
              {currentExerciseStep?.instruction}
            </p>
            
            {currentExerciseStep?.duration_seconds && (
              <p className="text-amber-400/60 text-sm">
                Take {currentExerciseStep.duration_seconds} seconds
              </p>
            )}
          </motion.div>
        </AnimatePresence>
        
        <button
          onClick={handleNext}
          className="px-8 py-3 bg-amber-500 text-white font-semibold rounded-full hover:bg-amber-400 transition-colors"
          data-testid="embodiment-next-btn"
        >
          {currentStepIndex < steps.length - 1 ? "Next" : "Complete"}
        </button>
      </div>
    </motion.div>
  );
};


/**
 * Regulation Step - Animated breathing/stillness practice
 */
const RegulationStep = ({ regulation, onComplete }) => {
  const [isActive, setIsActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(regulation?.duration_seconds || 120);
  const timerRef = useRef(null);

  useEffect(() => {
    if (isActive && timeRemaining > 0) {
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            setTimeout(onComplete, 500);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [isActive, onComplete]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!isActive) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex flex-col items-center justify-center min-h-[80vh] px-6"
      >
        <div className="text-center space-y-8 max-w-md">
          <div className="w-20 h-20 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto">
            <Wind className="w-10 h-10 text-teal-400" />
          </div>
          
          <div className="space-y-2">
            <p className="text-white/40 text-xs uppercase tracking-[0.2em]">Regulation</p>
            <h2 className="text-white text-2xl font-heading">{regulation?.title}</h2>
            <p className="text-white/50 text-sm">{regulation?.instruction}</p>
          </div>
          
          <button
            onClick={() => setIsActive(true)}
            className="px-8 py-4 bg-teal-500 text-white font-semibold rounded-full hover:bg-teal-400 transition-all"
            data-testid="start-regulation-btn"
          >
            Begin Practice
          </button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <div className="text-center space-y-8">
        {/* Breathing animation */}
        <motion.div
          animate={{ 
            scale: [1, 1.2, 1],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="w-48 h-48 rounded-full bg-teal-500/20 flex items-center justify-center mx-auto border border-teal-400/30"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.15, 1],
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            className="w-32 h-32 rounded-full bg-teal-500/30 flex items-center justify-center"
          >
            <span className="text-teal-300 text-3xl font-heading">
              {formatTime(timeRemaining)}
            </span>
          </motion.div>
        </motion.div>
        
        <p className="text-white/60 text-sm">
          {regulation?.instruction}
        </p>
        
        <button
          onClick={() => {
            clearInterval(timerRef.current);
            onComplete();
          }}
          className="text-teal-400/60 text-sm hover:text-teal-400 transition-colors"
        >
          Skip
        </button>
      </div>
    </motion.div>
  );
};


/**
 * Lesson Integration Audio Step
 * 
 * A guided immersion audio where Michael speaks in more depth on the day's lesson.
 * NOT an affirmation player or subliminal - just Michael's voice with theta ambient music.
 * 
 * Purpose: Bridge between intellectual understanding (video) and embodied experience (embodiment)
 */
const IntegrationAudioStep = ({ integrationAudio, onComplete }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const audioRef = useRef(null);
  const ambientRef = useRef(null);
  
  const duration = integrationAudio?.duration_seconds || 300;

  const startAudio = () => {
    setIsPlaying(true);
    
    // If we have audio URLs, we'd play them here
    // For now, simulate progress
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 1500);
          return 100;
        }
        return prev + (100 / duration);
      });
    }, 1000);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <div className="text-center space-y-8 max-w-md">
        {!isPlaying ? (
          <>
            {/* Entry state - invite to receive */}
            <motion.div
              animate={{ 
                boxShadow: ["0 0 40px rgba(99, 102, 241, 0.2)", "0 0 70px rgba(99, 102, 241, 0.35)", "0 0 40px rgba(99, 102, 241, 0.2)"]
              }}
              transition={{ duration: 4, repeat: Infinity }}
              className="w-28 h-28 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-600/20 
                        flex items-center justify-center mx-auto border border-indigo-400/30"
            >
              <Volume2 className="w-14 h-14 text-indigo-400" />
            </motion.div>
            
            <div className="space-y-3">
              <p className="text-indigo-400/60 text-xs uppercase tracking-[0.2em]">Lesson Integration</p>
              <h2 className="text-white text-2xl font-heading">{integrationAudio?.title || "Deeper Integration"}</h2>
              <p className="text-white/50 text-sm leading-relaxed">
                {integrationAudio?.description || "Close your eyes and let today's lesson land at a deeper level"}
              </p>
            </div>
            
            <div className="space-y-4">
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={startAudio}
                className="px-10 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 
                          text-white font-semibold rounded-full shadow-lg shadow-indigo-500/30
                          hover:shadow-indigo-500/50 transition-all"
                data-testid="start-integration-audio-btn"
              >
                Begin Integration
              </motion.button>
              
              <p className="text-white/30 text-xs">~{Math.round(duration / 60)} minute guided experience</p>
            </div>
          </>
        ) : (
          <>
            {/* Playing state - calm, spacious, receptive */}
            <motion.div
              animate={{ 
                scale: [1, 1.05, 1],
                opacity: [0.7, 1, 0.7]
              }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
              className="w-36 h-36 rounded-full bg-gradient-to-br from-indigo-500/30 to-purple-600/20 
                        flex items-center justify-center mx-auto border border-indigo-400/20"
            >
              <motion.div
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="w-24 h-24 rounded-full bg-indigo-500/20 flex items-center justify-center"
              >
                <Volume2 className="w-10 h-10 text-indigo-300" />
              </motion.div>
            </motion.div>
            
            <div className="space-y-2">
              <p className="text-indigo-300/80 text-lg italic">Receiving...</p>
              <p className="text-white/40 text-sm">
                {formatTime(Math.floor((progress / 100) * duration))} / {formatTime(duration)}
              </p>
            </div>
            
            {/* Subtle progress bar */}
            <div className="w-64 h-1 bg-white/10 rounded-full mx-auto overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"
                style={{ width: `${progress}%` }}
              />
            </div>
            
            <button
              onClick={onComplete}
              className="text-indigo-400/50 text-sm hover:text-indigo-400 transition-colors"
            >
              Skip
            </button>
          </>
        )}
      </div>
    </motion.div>
  );
};


/**
 * Reflection Step - Journal input
 */
const ReflectionStep = ({ prompt, dayNumber, onComplete }) => {
  const [text, setText] = useState("");
  const [emotionalState, setEmotionalState] = useState(5);

  const handleSubmit = () => {
    if (text.trim()) {
      onComplete(text);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col min-h-[80vh] px-6 py-8"
    >
      <div className="max-w-md mx-auto w-full space-y-6">
        <div className="text-center space-y-2">
          <p className="text-white/40 text-xs uppercase tracking-[0.2em]">Reflection</p>
          <h2 className="text-white text-xl font-heading">Journal</h2>
        </div>
        
        {/* Prompt */}
        <div className="bg-white/5 rounded-xl p-4 border border-emerald-500/20">
          <MessageSquare className="w-5 h-5 text-emerald-400 mb-2" />
          <p className="text-white/80 text-sm leading-relaxed">{prompt}</p>
        </div>
        
        {/* Text input */}
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Write your reflection..."
          className="w-full h-40 bg-white/5 border border-white/10 rounded-xl p-4 text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50 resize-none"
          data-testid="reflection-input"
        />
        
        {/* Emotional state slider */}
        <div className="space-y-2">
          <p className="text-white/40 text-xs">How do you feel? (1-10)</p>
          <div className="flex items-center gap-4">
            <span className="text-white/40 text-xs">Low</span>
            <input
              type="range"
              min="1"
              max="10"
              value={emotionalState}
              onChange={(e) => setEmotionalState(parseInt(e.target.value))}
              className="flex-1 accent-emerald-500"
            />
            <span className="text-white/40 text-xs">High</span>
            <span className="text-emerald-400 font-bold">{emotionalState}</span>
          </div>
        </div>
        
        <button
          onClick={handleSubmit}
          disabled={!text.trim()}
          className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-reflection-btn"
        >
          Save Reflection
        </button>
      </div>
    </motion.div>
  );
};


/**
 * Intention Step - Daily intention lock-in
 */
const IntentionStep = ({ prompt, dayNumber, onComplete }) => {
  const [text, setText] = useState("");

  const handleSubmit = () => {
    if (text.trim()) {
      onComplete(text);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <div className="max-w-md w-full space-y-8 text-center">
        <div className="space-y-2">
          <Target className="w-12 h-12 text-amber-400 mx-auto" />
          <p className="text-white/40 text-xs uppercase tracking-[0.2em]">Intention</p>
          <h2 className="text-white text-xl font-heading">Lock In Your Intent</h2>
        </div>
        
        <p className="text-white/60 text-lg">{prompt}</p>
        
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Complete your intention..."
          className="w-full bg-white/5 border border-white/10 rounded-xl p-4 text-white text-center placeholder-white/30 focus:outline-none focus:border-amber-500/50"
          data-testid="intention-input"
        />
        
        <button
          onClick={handleSubmit}
          disabled={!text.trim()}
          className="w-full py-3 bg-amber-500 text-white font-semibold rounded-full hover:bg-amber-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="submit-intention-btn"
        >
          Set Intention
        </button>
      </div>
    </motion.div>
  );
};


/**
 * Completion Step - Day complete celebration
 */
const CompletionStep = ({ dayNumber, totalDays, nightRitualLink, onContinue }) => {
  const navigate = useNavigate();
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center justify-center min-h-[80vh] px-6"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="text-center space-y-8 max-w-md"
      >
        {/* Celebration glow */}
        <motion.div
          animate={{ 
            boxShadow: ["0 0 60px rgba(16, 185, 129, 0.3)", "0 0 100px rgba(16, 185, 129, 0.5)", "0 0 60px rgba(16, 185, 129, 0.3)"]
          }}
          transition={{ duration: 2, repeat: Infinity }}
          className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto"
        >
          <Check className="w-12 h-12 text-emerald-400" />
        </motion.div>
        
        <div className="space-y-2">
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-white text-2xl font-heading"
          >
            Integration Complete
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-emerald-400/60"
          >
            Day {dayNumber} of {totalDays}
          </motion.p>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          className="space-y-4"
        >
          {/* Night Ritual Link - Updated to use Abundance Night Journey */}
          <button
            onClick={() => navigate(`/journey/abundance/night/${dayNumber}`)}
            className="w-full flex items-center justify-center gap-3 py-3 bg-indigo-500/20 border border-indigo-500/30 rounded-full text-indigo-300 hover:bg-indigo-500/30 transition-colors"
            data-testid="night-ritual-link"
          >
            <Moon className="w-5 h-5" />
            Begin Tonight's Integration
          </button>
          
          <button
            onClick={onContinue}
            className="w-full py-3 bg-emerald-500 text-white font-semibold rounded-full hover:bg-emerald-400 transition-colors"
            data-testid="continue-journey-btn"
          >
            Return to Journey
          </button>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};


export default JourneyDayPage;
